import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";

import { DeleteButton } from "common/components/DeleteButton/DeleteButton";
import { useAppDialog } from "hooks/useAppDialog";
import { useUpdateSelectedOrganizationApiToken } from "hooks/useUpdateSelectedOrganizationApiToken";
import { OrganizationApiToken } from "types/Organization";

interface DeleteSettingProps {
  row: Record<string, string>;
  token: OrganizationApiToken;
}

export const DeleteSetting = ({
  row,
  token,
}: DeleteSettingProps): JSX.Element => {
  const { t } = useTranslation();
  const { isLoading, isSuccess, onUpdateApiToken } =
    useUpdateSelectedOrganizationApiToken({
      tokenId: token.id,
    });
  const { Dialog, openDialog, dialogProps, closeDialog } = useAppDialog();

  const onDeleteClick = useCallback(
    (event) => {
      event.stopPropagation();
      openDialog();
    },
    [openDialog]
  );

  const onDeleteConfirm = useCallback(() => {
    const { [row.key]: _, ...settings } = token.settings as Record<
      string,
      string
    >;
    return onUpdateApiToken({
      id: token.id,
      name: token.name,
      description: token.description,
      permissions: token.permissions.map((permission) => permission),
      settings,
    });
  }, [onUpdateApiToken, token, row]);

  useEffect(() => {
    if (isSuccess) {
      closeDialog();
    }
  }, [isSuccess, closeDialog]);

  return (
    <>
      <DeleteButton onClick={onDeleteClick} />
      <Dialog
        title={t("DeleteSetting")}
        loading={isLoading}
        buttonColor="error"
        actionText={t("Delete")}
        onSubmit={onDeleteConfirm}
        maxWidth="xs"
        {...dialogProps}
      >
        <Typography>{t("DeleteCopy")}</Typography>
      </Dialog>
    </>
  );
};
